import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, ChartConfiguration, ChartType, registerables } from 'chart.js';
import { DashboardMockupService, YearlyData, PopularCourse } from '../../services/dashboard-mockup.service';

Chart.register(...registerables);

type DashboardView = 'dashboard' | 'courses' | 'reports' | 'settings';

@Component({
  selector: 'app-staff-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, StaffHeaderComponent, StaffNavComponent, BaseChartDirective],
  templateUrl: './staff-dashboard.component.html',
  styleUrl: './staff-dashboard.component.scss'
})
export class StaffDashboardComponent implements OnInit, OnDestroy {
  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;
  currentView: DashboardView = 'dashboard';
  private userSubscription: Subscription | undefined;

  // Year Filter
  availableYears: number[] = [];
  selectedYear: number = 2569;

  // Current Year Data
  currentData: YearlyData | null = null;

  // Popular Courses
  popularCourses: PopularCourse[] = [];

  // Historical Data
  courseHistoricalData: any[] = [];
  examHistoricalData: any[] = [];

  // Chart Configurations - Membership
  membershipPieChartData: ChartConfiguration<'pie'>['data'] = {
    labels: [],
    datasets: [{ data: [], backgroundColor: [] }]
  };
  membershipPieChartOptions: ChartConfiguration<'pie'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = (context.dataset.data as number[]).reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    }
  };
  membershipPieChartType = 'pie' as const;

  // Chart Configurations - Organization
  organizationPieChartData: ChartConfiguration<'pie'>['data'] = {
    labels: [],
    datasets: [{ data: [], backgroundColor: [] }]
  };
  organizationPieChartOptions: ChartConfiguration<'pie'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = (context.dataset.data as number[]).reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    }
  };
  organizationPieChartType = 'pie' as const;

  // Chart Configurations - Appraiser Type
  appraiserTypePieChartData: ChartConfiguration<'pie'>['data'] = {
    labels: [],
    datasets: [{ data: [], backgroundColor: [] }]
  };
  appraiserTypePieChartOptions: ChartConfiguration<'pie'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = (context.dataset.data as number[]).reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    }
  };
  appraiserTypePieChartType = 'pie' as const;

  // Chart Configurations - Appraiser Revenue
  appraiserRevenuePieChartData: ChartConfiguration<'pie'>['data'] = {
    labels: [],
    datasets: [{ data: [], backgroundColor: [] }]
  };
  appraiserRevenuePieChartOptions: ChartConfiguration<'pie'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            return `${label}: ฿${value.toLocaleString()}`;
          }
        }
      }
    }
  };
  appraiserRevenuePieChartType = 'pie' as const;

  // Chart Configurations - Course & CPD Bar Chart
  courseBarChartData: ChartConfiguration<'bar'>['data'] = {
    labels: [],
    datasets: []
  };
  courseBarChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${(context.parsed.y || 0).toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      y: { beginAtZero: true }
    }
  };
  courseBarChartType = 'bar' as const;

  // Chart Configurations - Course Revenue Bar Chart
  courseRevenueBarChartData: ChartConfiguration<'bar'>['data'] = {
    labels: [],
    datasets: []
  };
  courseRevenueBarChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `รายได้: ฿${(context.parsed.y || 0).toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      y: { beginAtZero: true }
    }
  };
  courseRevenueBarChartType = 'bar' as const;

  // Chart Configurations - Exam Bar Chart
  examBarChartData: ChartConfiguration<'bar'>['data'] = {
    labels: [],
    datasets: []
  };
  examBarChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `จำนวนครั้ง: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 2 } }
    }
  };
  examBarChartType = 'bar' as const;

  // Chart Configurations - Exam Type Pie Chart
  examTypePieChartData: ChartConfiguration<'pie'>['data'] = {
    labels: [],
    datasets: [{ data: [], backgroundColor: [] }]
  };
  examTypePieChartOptions: ChartConfiguration<'pie'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = (context.dataset.data as number[]).reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} ครั้ง (${percentage}%)`;
          }
        }
      }
    }
  };
  examTypePieChartType = 'pie' as const;

  constructor(
    private authService: AuthService,
    private router: Router,
    private dashboardService: DashboardMockupService
  ) {}

  ngOnInit(): void {
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });

    // Initialize dashboard data
    this.availableYears = this.dashboardService.getAvailableYears();
    this.selectedYear = 2569; // Current year
    this.loadDashboardData();
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  onLogout(): void {
    this.authService.logout();
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onNavigate(route: string): void {
    // Close mobile menu when navigating
    this.closeMobileMenu();
    
    switch (route) {
      case 'courses':
        this.currentView = 'courses';
        break;
      case 'reports':
        this.currentView = 'reports';
        break;
      case 'settings':
        this.currentView = 'settings';
        break;
      default:
        this.currentView = 'dashboard';
        break;
    }
  }

  navigateToUsers(): void {
    this.router.navigate(['/staff/user-management']);
  }

  navigateToCourses(): void {
    this.currentView = 'courses';
  }

  navigateToReports(): void {
    this.currentView = 'reports';
  }

  navigateToSettings(): void {
    this.currentView = 'settings';
  }

  navigateToDashboard(): void {
    this.currentView = 'dashboard';
  }

  onYearChange(): void {
    this.loadDashboardData();
  }

  loadDashboardData(): void {
    this.currentData = this.dashboardService.getDataByYear(this.selectedYear) || null;
    this.popularCourses = this.dashboardService.getTopPopularCourses();
    this.courseHistoricalData = this.dashboardService.getCourseHistoricalData();
    this.examHistoricalData = this.dashboardService.getExamHistoricalData();

    if (this.currentData) {
      this.updateMembershipChart();
      this.updateOrganizationChart();
      this.updateAppraiserCharts();
      this.updateCourseCharts();
      this.updateExamCharts();
    }
  }

  private updateMembershipChart(): void {
    if (!this.currentData) return;

    const data = this.currentData.membership.byType;
    this.membershipPieChartData = {
      labels: ['วิสามัญ', 'สามัญ', 'ตลอดชีพ', 'สมาชิกสมทบ'],
      datasets: [{
        data: [
          data.extraordinary,
          data.ordinary,
          data.lifetime,
          data.associate
        ],
        backgroundColor: ['#667eea', '#48bb78', '#ed8936', '#f56565']
      }]
    };
  }

  private updateOrganizationChart(): void {
    if (!this.currentData) return;

    const data = this.currentData.organization.byType;
    this.organizationPieChartData = {
      labels: ['บริษัทประเมิน', 'ธนาคาร', 'ราชการ', 'อื่นๆ'],
      datasets: [{
        data: [
          data.appraisalCompany,
          data.bank,
          data.government,
          data.other
        ],
        backgroundColor: ['#667eea', '#48bb78', '#ed8936', '#f56565']
      }]
    };
  }

  private updateAppraiserCharts(): void {
    if (!this.currentData) return;

    // Appraiser Type Chart
    const typeData = this.currentData.appraiser.byType;
    this.appraiserTypePieChartData = {
      labels: ['วิสามัญ', 'สามัญ', 'วุฒิ', 'ไม่มีบัตร'],
      datasets: [{
        data: [
          typeData.extraordinary,
          typeData.ordinary,
          typeData.senior,
          typeData.none
        ],
        backgroundColor: ['#667eea', '#48bb78', '#ed8936', '#cbd5e0']
      }]
    };

    // Appraiser Revenue Chart
    const revenueData = this.currentData.appraiser.revenue.byType;
    this.appraiserRevenuePieChartData = {
      labels: ['วิสามัญ', 'สามัญ', 'วุฒิ'],
      datasets: [{
        data: [
          revenueData.extraordinary,
          revenueData.ordinary,
          revenueData.senior
        ],
        backgroundColor: ['#667eea', '#48bb78', '#ed8936']
      }]
    };
  }

  private updateCourseCharts(): void {
    // Course & CPD Bar Chart (5 years)
    const years = this.courseHistoricalData.map(d => `ปี ${d.year}`);
    const courses = this.courseHistoricalData.map(d => d.courses);
    const cpd = this.courseHistoricalData.map(d => d.cpd);

    this.courseBarChartData = {
      labels: years,
      datasets: [
        {
          label: 'จำนวนคอร์ส',
          data: courses,
          backgroundColor: '#667eea'
        },
        {
          label: 'จำนวน CPD',
          data: cpd,
          backgroundColor: '#48bb78'
        }
      ]
    };

    // Course Revenue Bar Chart (5 years)
    const revenue = this.courseHistoricalData.map(d => d.revenue);
    this.courseRevenueBarChartData = {
      labels: years,
      datasets: [
        {
          label: 'รายได้',
          data: revenue,
          backgroundColor: '#ed8936'
        }
      ]
    };
  }

  private updateExamCharts(): void {
    if (!this.currentData) return;

    // Exam Historical Bar Chart (5 years)
    const years = this.examHistoricalData.map(d => `ปี ${d.year}`);
    const exams = this.examHistoricalData.map(d => d.exams);

    this.examBarChartData = {
      labels: years,
      datasets: [
        {
          label: 'จำนวนครั้งที่จัดสอบ',
          data: exams,
          backgroundColor: '#667eea'
        }
      ]
    };

    // Exam Type Pie Chart (Current Year)
    const examData = this.currentData.exam.byType;
    this.examTypePieChartData = {
      labels: ['วิสามัญ', 'สามัญ', 'วุฒิ'],
      datasets: [{
        data: [
          examData.extraordinary,
          examData.ordinary,
          examData.senior
        ],
        backgroundColor: ['#667eea', '#48bb78', '#ed8936']
      }]
    };
  }

  formatCurrency(value: number): string {
    return `฿${value.toLocaleString()}`;
  }

  formatNumber(value: number): string {
    return value.toLocaleString();
  }
} 